import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import uniqBy from 'lodash/uniqBy';
import cx from 'classnames';

import { fullName, capitalize } from 'utils';
import Container from 'common/container';
import Button from 'common/button';
import ContributeContext from '../context';
import ContributeHeader from '../header';
import StepTitle from '../step-title';
import SelectFundraiserDropdown from './dropdown';

const GET_FUNDRAISER = gql`
    query GetFundaiser($username: String!, $campaignId: ID!) {
        fundraiserByUsername(username: $username, campaignId: $campaignId) {
            id
            user {
                id
                firstName
                lastName
                username
            }
        }
    }
`;

const SelectFundraiser = ({ onFundraiserChange, onContinue }) => {
    const { campaign, fundraisers, referrer, type } = useContext(ContributeContext);

    const itemQuery = useQuery(GET_FUNDRAISER, {
        variables: {
            username: referrer,
            campaignId: campaign.id,
        },
        skip: !referrer,
    });
    const itemResult = itemQuery.data?.fundraiserByUsername;

    // The list query won't return any fundraisers marked as "hidden"
    // but the initial referrer may be set to such a user. To safeguard
    // this case, we manually merge in the selected referrer
    const fundraiserList = uniqBy([...(itemResult ? [itemResult] : []), ...fundraisers], 'id');

    // If the pre-set referrer isn't actually a fundraiser, reset it
    useEffect(() => {
        const match = fundraiserList.find(x => x.user.username === referrer);
        if (!itemQuery.loading && !match) onFundraiserChange(null);
    }, [itemQuery.loading, referrer]);

    return (
        <Container className="pt-8 pb-16 flex-1 flex flex-col justify-start max-w-xl">
            <div className="md:my-4">
                <ContributeHeader />
            </div>
            <div className="mt-12 rounded-lg lg:bg-white lg:shadow lg:p-16">
                <StepTitle title="Give fundraising credit to a team member?" />
                <div className="mt-8">
                    <SelectFundraiserDropdown
                        selected={referrer}
                        fundraisers={fundraiserList.map(x => ({
                            name: fullName(x.user.firstName, x.user.lastName),
                            username: x.user.username,
                        }))}
                        onChange={onFundraiserChange}
                    />
                </div>
                <div className="mt-12 w-full max-w-sm mx-auto">
                    <Button
                        size="md"
                        color="green"
                        className={cx('mt-8 block w-full text-16 font-medium', {
                            'pointer-events-none': !type,
                        })}
                        onClick={onContinue}
                    >
                        Continue {type ? `to ${capitalize(type)}` : ''}
                    </Button>
                </div>
            </div>
        </Container>
    );
};

SelectFundraiser.propTypes = {
    onFundraiserChange: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
};

export default SelectFundraiser;
